import React, { useEffect, useState } from 'react';
import { X, Copy, Check } from 'lucide-react';
import { freeFetch } from './api';
import {
  forcedHint,
  isInAppBrowser,
  isIosInstallable,
  isIOSDevice,
  getIosBrowserName,
} from './environment';

// Collects the exact signals our detection heuristics rely on, so we can
// compare real devices (Yahoo/Gmail/Outlook on iPhone) against what the
// heuristics assume. Nothing here is persisted — it only renders while
// ?hint=debug is in the URL.
// Renders a real .free-input-text input off-screen and reads back the font-size
// the browser actually applied, so we can confirm the 16px fix took effect on
// this exact device instead of just trusting the CSS source.
function measureInputFontSize() {
  const probe = document.createElement('input');
  probe.className = 'free-input-text';
  probe.style.position = 'absolute';
  probe.style.top = '-9999px';
  probe.style.left = '-9999px';
  document.body.appendChild(probe);
  const fontSize = window.getComputedStyle(probe).fontSize;
  document.body.removeChild(probe);
  return fontSize;
}

function collectSyncFacts() {
  const ua = navigator.userAgent || '';
  const env = { ua, hasStandaloneFlag: 'standalone' in navigator, maxTouchPoints: navigator.maxTouchPoints || 0 };
  const handlers = window.webkit?.messageHandlers ? Object.keys(window.webkit.messageHandlers) : [];
  const displayMode = window.matchMedia('(display-mode: standalone)').matches
    ? 'standalone'
    : window.matchMedia('(display-mode: fullscreen)').matches
    ? 'fullscreen'
    : 'browser';

  return {
    inputFontSize: measureInputFontSize(),
    pointerFine: window.matchMedia('(pointer: fine)').matches,
    hoverCapable: window.matchMedia('(hover: hover)').matches,
    userAgent: ua,
    isIOSDevice: isIOSDevice(env),
    hasStandaloneFlag: env.hasStandaloneFlag,
    navigatorStandalone: String(window.navigator.standalone),
    webkitMessageHandlers: handlers.length ? handlers.join(', ') : 'none',
    innerHeight: window.innerHeight,
    screenHeight: window.screen.height,
    heightRatio: (window.innerHeight / window.screen.height).toFixed(2),
    displayMode,
    documentCookiePresent: document.cookie.length > 0,
    documentCookieLength: document.cookie.length,
    detectInApp: isInAppBrowser(),
    detectIosInstallable: isIosInstallable(),
    iosBrowserName: getIosBrowserName(),
  };
}

const Row = ({ label, value, testId }) => (
  <div className="flex items-start justify-between gap-3 py-1.5 border-b border-mono-line/60" data-testid={testId}>
    <span className="text-mono-mute text-xs font-heading uppercase tracking-[0.1em] shrink-0">{label}</span>
    <span className="text-white text-xs font-mono text-right break-all">{String(value)}</span>
  </div>
);

export default function DebugReadout() {
  const [visible, setVisible] = useState(() => forcedHint() === 'debug');
  const [facts, setFacts] = useState(null);
  const [sessionCheck, setSessionCheck] = useState('checking...');
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!visible) return;
    setFacts(collectSyncFacts());
    freeFetch('/auth/session')
      .then((data) => setSessionCheck(data.authenticated ? `authenticated as ${data.email}` : 'not authenticated (no valid cookie)'))
      .catch((err) => setSessionCheck(`request failed: ${err.message}`));
  }, [visible]);

  if (!visible || !facts) return null;

  const reportText = Object.entries({ ...facts, sessionCheck })
    .map(([k, v]) => `${k}: ${v}`)
    .join('\n');

  const copy = async () => {
    try {
      await navigator.clipboard.writeText(reportText);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      /* clipboard unavailable in this webview — text is still selectable */
    }
  };

  return (
    <div
      className="fixed inset-x-0 top-0 z-[999] max-h-[80vh] overflow-y-auto bg-mono-base border-b-2 border-crimson shadow-2xl"
      data-testid="debug-readout-panel"
    >
      <div className="flex items-center justify-between px-4 py-3 bg-mono-card border-b border-mono-line sticky top-0">
        <span className="text-white font-heading text-xs font-bold uppercase tracking-[0.2em]">
          Debug Readout
        </span>
        <div className="flex items-center gap-3">
          <button
            onClick={copy}
            data-testid="debug-readout-copy-btn"
            className="flex items-center gap-1.5 text-xs font-heading uppercase tracking-[0.1em] text-mono-mute hover:text-white transition-colors"
          >
            {copied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
            {copied ? 'Copied' : 'Copy report'}
          </button>
          <button
            onClick={() => setVisible(false)}
            aria-label="Close"
            data-testid="debug-readout-close-btn"
            className="text-mono-mute hover:text-white transition-colors"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>
      <div className="px-4 py-2">
        <Row label="User Agent" value={facts.userAgent} testId="debug-row-ua" />
        <Row label="Is iOS Device" value={facts.isIOSDevice} testId="debug-row-is-ios" />
        <Row label="Live input font-size (.free-input-text)" value={facts.inputFontSize} testId="debug-row-input-font-size" />
        <Row label="pointer: fine" value={facts.pointerFine} testId="debug-row-pointer-fine" />
        <Row label="hover: hover" value={facts.hoverCapable} testId="debug-row-hover-capable" />
        <Row label="iOS Browser Name" value={facts.iosBrowserName} testId="debug-row-browser-name" />
        <Row label="'standalone' in navigator" value={facts.hasStandaloneFlag} testId="debug-row-standalone-flag" />
        <Row label="navigator.standalone" value={facts.navigatorStandalone} testId="debug-row-nav-standalone" />
        <Row label="window.webkit.messageHandlers" value={facts.webkitMessageHandlers} testId="debug-row-webkit-handlers" />
        <Row label="innerHeight / screen.height" value={`${facts.innerHeight} / ${facts.screenHeight} (${facts.heightRatio})`} testId="debug-row-heights" />
        <Row label="display-mode" value={facts.displayMode} testId="debug-row-display-mode" />
        <Row label="document.cookie present" value={`${facts.documentCookiePresent} (len ${facts.documentCookieLength})`} testId="debug-row-cookie" />
        <Row label="detectInApp() result" value={facts.detectInApp} testId="debug-row-detect-inapp" />
        <Row label="detectIosInstallable() result" value={facts.detectIosInstallable} testId="debug-row-detect-installable" />
        <Row label="Session cookie round-trip" value={sessionCheck} testId="debug-row-session-check" />
        <p className="text-mono-mute text-[11px] font-body leading-relaxed mt-3 pb-2">
          document.cookie will not show the session cookie itself (it's httpOnly by design) — "Session cookie
          round-trip" is the real signal for whether cookies survive in this webview.
        </p>
        <p className="text-mono-mute text-[11px] font-body leading-relaxed pb-2">
          "Live input font-size" should read 16px on this iPhone. If it still reads a value under 16px, the
          installed app has not picked up the fix yet — check the build stamp at the bottom of the dashboard
          against the latest one.
        </p>
      </div>
    </div>
  );
}
